// Master Apps · SelectMenu — dropdown próprio para substituir <select> nativo.
//
// O motivo de existir: a LISTA de um <select> é desenhada pelo sistema
// operacional — nenhum CSS alcança as <option>, e o popup sai com a cara de
// HTML padrão destoando do resto do produto (foi o caso dos "De/Até" da
// apresentação). Aqui o painel é nosso: mesma moldura, mesma tipografia,
// hover/seleção com o acento da tela.
//
// Comportamento coberto: clique abre/fecha, clique-fora fecha, Esc fecha (sem
// vazar para o Esc de modal/deck por trás), setas navegam, Home/End pulam,
// Enter/Espaço selecionam, Tab fecha e segue o fluxo. Foco permanece no botão
// (padrão combobox colapsado), com aria-expanded/activedescendant para leitor.
//
// O painel vai para um PORTAL com `position: fixed` — mesma técnica do
// `MultiSelect` e do `InfoPopover`. Enquanto era `position: absolute` ele era
// recortado pelo primeiro ancestral com `overflow`, e no inventário esse
// ancestral é o rolador lateral da tabela: com duas linhas no resultado a
// tabela é baixa e o filtro de Responsável saía cortado pela metade em vez de
// flutuar por cima. Fixed fora da árvore da tabela não tem quem o recorte, e a
// posição é remedida no scroll (em CAPTURA, para pegar o rolador da tabela) e
// no resize, senão o painel ficaria parado enquanto a coluna anda.
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { fontSans } from '@/lib/theme';

/** Respiro mínimo entre o painel e a borda da janela. */
const EDGE = 8;
/** Vão entre o gatilho e o painel. */
const GAP = 4;
/** Altura desejada do painel; o espaço real na tela pode reduzi-la. */
const DESIRED_H = 236;
/** Abaixo disto o painel prefere SUBIR a se espremer embaixo do gatilho. */
const MIN_H = 160;
/** Piso da altura quando nem para cima nem para baixo há espaço decente. */
const FLOOR_H = 120;

interface PanelPos {
  top?: number;
  bottom?: number;
  left: number;
  /** O painel nunca fica mais estreito que o gatilho. */
  minWidth: number;
  maxWidth: number;
  maxHeight: number;
}

function samePos(a: PanelPos | null, b: PanelPos): boolean {
  return (
    a !== null &&
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.minWidth === b.minWidth &&
    a.maxWidth === b.maxWidth &&
    a.maxHeight === b.maxHeight
  );
}

/**
 * Onde o painel é montado. `document.body` na vida normal; o elemento em TELA
 * CHEIA quando existe um — o deck da apresentação chama `requestFullscreen` na
 * própria div, e o navegador só pinta a subárvore do elemento em fullscreen:
 * um portal no body sumiria da tela justamente durante a apresentação.
 */
function portalHost(): HTMLElement {
  const fs = document.fullscreenElement;
  return fs instanceof HTMLElement ? fs : document.body;
}

/**
 * Valor string OU number: os "De/Até" da apresentação e o "Por página" guardam
 * números; os filtros de coluna do inventário guardam o texto do filtro (com
 * `''` fazendo o papel do "Todos" da <option> vazia de antes).
 */
export interface SelectMenuOption<T extends string | number = number> {
  value: T;
  label: string;
}

/** Cores/medidas do dono — o lobby usa a paleta do tema; o deck, o visual dele. */
export interface SelectMenuTone {
  bg: string;
  text: string;
  border: string;
  /** Borda no hover/aberto (um sopro do acento ou branco no deck). */
  borderActive: string;
  panelBg: string;
  panelBorder: string;
  /** Fundo da opção sob o cursor/teclado. */
  optionHover: string;
  /** Cor do texto da opção SELECIONADA (o marcador usa a mesma). */
  optionSelected: string;
  muted: string;
  height?: number;
  fontSize?: number;
  radius?: number;
  minWidth?: number;
  /** `'100%'` faz o controle preencher a célula (linha de filtros da tabela). */
  width?: number | string;
  /** Tipografia do gatilho e das opções (padrão: fontSans). */
  fontFamily?: string;
  /** Peso do texto do gatilho (padrão 600; a linha de filtros usa 500). */
  fontWeight?: number;
}

export function SelectMenu<T extends string | number>({
  value,
  options,
  onChange,
  label,
  tone,
}: Readonly<{
  value: T;
  options: SelectMenuOption<T>[];
  onChange: (v: T) => void;
  /** Rótulo acessível (o botão mostra só o valor corrente). */
  label: string;
  tone: SelectMenuTone;
}>) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  // Posição do painel em coordenadas de janela. Null é o paint anterior à
  // medida: o painel fica invisível para não piscar no canto da tela.
  const [pos, setPos] = useState<PanelPos | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLDivElement | null>(null);
  const listId = useId();

  const height = tone.height ?? 32;
  const fontSize = tone.fontSize ?? 12;
  const radius = tone.radius ?? 7;
  const fontFamily = tone.fontFamily ?? fontSans;
  const fontWeight = tone.fontWeight ?? 600;
  // Controle compacto (linha de filtros, altura 26): respiro e chevron menores,
  // senão "Todos" não cabe na célula estreita e vira "Tod…".
  const compact = height <= 28;
  const selectedIndex = options.findIndex((o) => o.value === value);
  const current = options[selectedIndex];

  // Clique fora fecha. `mousedown` (não click): fechar antes de o clique do
  // outro controle disparar evita "clique fantasma" que abre dois painéis.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const alvo = e.target as Node;
      // O painel mora num PORTAL, fora do `rootRef`: sem checá-lo também, o
      // mousedown numa opção contaria como "clique fora", o menu fecharia antes
      // do clique chegar e a escolha se perderia.
      if (!rootRef.current?.contains(alvo) && !listRef.current?.contains(alvo)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  /**
   * Posiciona o painel pelo retângulo do GATILHO: abre para baixo, sobe quando
   * não couber e encosta na borda quando faltar espaço à direita. A largura
   * real do painel entra na conta (`offsetWidth`, que já vem do conteúdo) —
   * medir só o gatilho deixaria a lista, sempre mais larga, sair da tela.
   * `clientWidth/clientHeight` e não `innerWidth/innerHeight`: é o retângulo
   * que o `position: fixed` enxerga, já sem a barra de rolagem.
   */
  const measure = useCallback(() => {
    const root = rootRef.current;
    if (!root) return;
    const r = root.getBoundingClientRect();
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;
    const maxWidth = vw - 2 * EDGE;
    const below = vh - r.bottom - GAP - EDGE;
    const above = r.top - GAP - EDGE;
    const up = below < MIN_H && above > below;
    const w = Math.min(Math.max(listRef.current?.offsetWidth ?? 0, r.width), maxWidth);
    const left = Math.min(Math.max(EDGE, r.left), Math.max(EDGE, vw - w - EDGE));
    const next: PanelPos = {
      top: up ? undefined : r.bottom + GAP,
      bottom: up ? vh - r.top + GAP : undefined,
      left,
      minWidth: r.width,
      maxWidth,
      maxHeight: Math.min(DESIRED_H, Math.max(FLOOR_H, Math.floor(up ? above : below))),
    };
    setPos((prev) => (samePos(prev, next) ? prev : next));
  }, []);

  // Mede ANTES da pintura: sem isso o "Por página" do rodapé abriria para fora
  // da janela por um quadro.
  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    measure();
  }, [open, measure]);

  // O painel acompanha o gatilho enquanto está aberto. `scroll` na fase de
  // CAPTURA porque scroll não borbulha: o gatilho pode estar dentro de um
  // rolador (a tabela do inventário rola de lado) e, sem a captura, o painel
  // ficaria parado enquanto a coluna anda — outro defeito no lugar do antigo.
  useEffect(() => {
    if (!open) return;
    const onReflow = () => measure();
    window.addEventListener('resize', onReflow);
    window.addEventListener('scroll', onReflow, true);
    return () => {
      window.removeEventListener('resize', onReflow);
      window.removeEventListener('scroll', onReflow, true);
    };
  }, [open, measure]);

  // A opção realçada acompanha o teclado também fora da área visível do painel.
  useEffect(() => {
    if (!open || highlight < 0) return;
    listRef.current
      ?.querySelector(`[data-index="${highlight}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, highlight]);

  const openPanel = () => {
    setHighlight(Math.max(selectedIndex, 0));
    // Zera a posição antes de abrir — a medida da abertura anterior não pode
    // contaminar esta (o gatilho pode ter andado com o scroll).
    setPos(null);
    setOpen(true);
  };

  const pick = (index: number) => {
    const option = options[index];
    if (option) onChange(option.value);
    setOpen(false);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        openPanel();
      }
      return;
    }
    // Aberto: as teclas são do dropdown — não podem vazar para o Esc do modal
    // nem para as setas que trocam slide no deck atrás.
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => Math.min(options.length - 1, Math.max(0, h + delta)));
    } else if (e.key === 'Home' || e.key === 'End') {
      e.preventDefault();
      e.stopPropagation();
      setHighlight(e.key === 'Home' ? 0 : options.length - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      pick(highlight);
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  return (
    <div
      ref={rootRef}
      style={{ position: 'relative', display: 'inline-flex', width: tone.width, minWidth: 0 }}
    >
      <button
        type="button"
        // Combobox colapsado (padrão APG "select-only"): o papel vive no
        // gatilho — o implícito de <button> seria "button" e o teste/leitor
        // procura um combobox. activedescendant aponta a opção realçada.
        role="combobox"
        aria-label={label}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={open && highlight >= 0 ? `${listId}-opt-${highlight}` : undefined}
        onClick={() => (open ? setOpen(false) : openPanel())}
        onKeyDown={onKeyDown}
        style={{
          height,
          minWidth: tone.minWidth ?? 0,
          width: tone.width ? '100%' : undefined,
          padding: compact ? '0 6px 0 8px' : '0 8px 0 10px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: compact ? 4 : 6,
          borderRadius: radius,
          border: `1px solid ${open ? tone.borderActive : tone.border}`,
          background: tone.bg,
          color: tone.text,
          fontSize,
          fontWeight,
          fontFamily,
          cursor: 'pointer',
          outline: 'none',
          transition: 'border-color 80ms ease-out',
          whiteSpace: 'nowrap',
        }}
        onMouseEnter={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.currentTarget.style.borderColor = tone.borderActive;
        }}
        onMouseLeave={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!open) e.currentTarget.style.borderColor = tone.border;
        }}
      >
        {/* Ellipsis: na célula estreita do filtro um responsável comprido não
            pode empurrar a largura da coluna. */}
        <span style={{ flex: 1, textAlign: 'left', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {current?.label ?? '—'}
        </span>
        {/* chevron — gira quando aberto, no mesmo ritmo curto do resto */}
        <svg
          width={compact ? 10 : 12}
          height={compact ? 10 : 12}
          viewBox="0 0 24 24"
          fill="none"
          stroke={tone.muted}
          strokeWidth="2.4"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{
            transition: 'transform 80ms ease-out',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open &&
        createPortal(
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={label}
            style={{
              position: 'fixed',
              top: pos ? pos.top : 0,
              bottom: pos?.bottom,
              left: pos?.left ?? 0,
              // Enquanto não há medida o painel existe (para ser medido) mas
              // não aparece nem recebe clique.
              visibility: pos ? 'visible' : 'hidden',
              pointerEvents: pos ? 'auto' : 'none',
              // 8001: a mesma camada dos outros painéis da casa (MultiSelect,
              // InfoPopover), acima de modal e do chrome do deck.
              zIndex: 8001,
              minWidth: pos?.minWidth,
              // O teto de largura já vale no quadro da MEDIDA (por isso o
              // `calc`, e não `undefined`): assim o `offsetWidth` que a medida
              // lê já é o definitivo, e o clamp horizontal acerta de primeira.
              maxWidth: pos?.maxWidth ?? `calc(100vw - ${2 * EDGE}px)`,
              maxHeight: pos?.maxHeight ?? DESIRED_H,
              overflowY: 'auto',
              // Sem isto o `overflow-x` viraria `auto` por tabela e uma opção
              // comprida na janela estreita criaria barra lateral no painel —
              // o rótulo encurta com reticências em vez disso.
              overflowX: 'hidden',
              padding: 4,
              borderRadius: radius + 2,
              border: `1px solid ${tone.panelBorder}`,
              background: tone.panelBg,
              boxShadow: '0 12px 32px -8px rgba(0,0,0,0.45)',
            }}
          >
            {options.map((o, i) => {
              const selected = o.value === value;
              const lit = i === highlight;
              return (
                // <button> e não <div>: o teclado do combobox vive no gatilho, mas um
                // `div role="option"` com `onClick` é elemento não interativo com
                // handler de ponteiro — o bug S1082 — e ainda não é focável (S6852).
                // O botão nativo resolve os dois de graça (Enter/Espaço e foco) e
                // dispensa a supressão do lint que estava aqui. As quatro primeiras
                // declarações de estilo apagam a folha do navegador para o desenho
                // ficar idêntico ao do div.
                <button
                  key={o.value}
                  type="button"
                  role="option"
                  id={`${listId}-opt-${i}`}
                  aria-selected={selected}
                  data-index={i}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => pick(i)}
                  style={{
                    appearance: 'none',
                    border: 'none',
                    width: '100%',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '7px 9px',
                    borderRadius: radius - 2,
                    fontSize,
                    fontWeight: selected ? 700 : 500,
                    fontFamily,
                    color: selected ? tone.optionSelected : tone.text,
                    background: lit ? tone.optionHover : 'transparent',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {/* `minWidth: 0` + reticências: só entram em ação quando o
                    painel bate no teto de largura da janela estreita — no
                    desktop o painel cresce com o conteúdo e o rótulo sai
                    inteiro, como sempre saiu. */}
                  <span
                    style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {o.label}
                  </span>
                  {selected && (
                    <svg
                      width={12}
                      height={12}
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke={tone.optionSelected}
                      strokeWidth="2.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>,
          portalHost(),
        )}
    </div>
  );
}
